function normalizeActionEntry(action) {
  return {
    ...action,
    checked: action.checked !== 0
  };
}

function normalizeChildEntry(child) {
  return {
    ...child,
    checked: child.checked !== 0,
    actions: Array.isArray(child.actions) ? child.actions.map(normalizeActionEntry) : []
  };
}

function normalizePermissionTree(tree) {
  return Array.isArray(tree)
    ? tree.map(item => ({
        ...item,
        checked: item.checked !== 0,
        children: Array.isArray(item.children) ? item.children.map(normalizeChildEntry) : []
      }))
    : [];
}

function buildChildLookup(permissionTree) {
  return normalizePermissionTree(permissionTree).reduce((result, group) => {
    group.children.forEach(child => {
      const key = child.path || child.uri;
      if (key) {
        result[key] = {
          group,
          child
        };
      }
    });

    return result;
  }, {});
}

function buildActionMapFromCatalog(catalog) {
  return catalog.reduce((result, group) => {
    group.children.forEach(child => {
      (child.actions || []).forEach(action => {
        result[action.uri] = true;
      });
    });

    return result;
  }, {});
}

function mergeCatalogChild(child, backendEntry) {
  const backendChild = backendEntry && backendEntry.child ? backendEntry.child : null;
  const actionLookup = backendChild
    ? backendChild.actions.reduce((result, action) => {
        result[action.uri] = action;
        return result;
      }, {})
    : {};

  return {
    code: backendChild && backendChild.code ? backendChild.code : child.code,
    title: backendChild && backendChild.name ? backendChild.name : child.title,
    path: child.path,
    permissionUri: child.permissionUri,
    actions: (child.actions || []).map(action => ({
      code: actionLookup[action.uri] && actionLookup[action.uri].code
        ? actionLookup[action.uri].code
        : action.code,
      uri: action.uri,
      name: actionLookup[action.uri] && actionLookup[action.uri].name
        ? actionLookup[action.uri].name
        : action.name,
      checked: actionLookup[action.uri]
        ? Boolean(actionLookup[action.uri].checked)
        : true
    }))
  };
}

const DASHBOARD_GROUP_URI = 'overview-control';
const DASHBOARD_CHILD_URI = 'dashboard-view';

function createDashboardMenu(catalog = []) {
  const group = catalog.find(item => item.uri === DASHBOARD_GROUP_URI);

  if (!group) {
    return null;
  }

  const child = (group.children || []).find(item => item.permissionUri === DASHBOARD_CHILD_URI);

  if (!child) {
    return null;
  }

  return {
    title: group.title,
    uri: group.uri,
    icon: group.icon,
    children: [
      {
        title: child.title,
        path: child.path,
        permissionUri: child.permissionUri,
        actions: (child.actions || []).map(action => ({
          code: action.code,
          uri: action.uri,
          name: action.name,
          checked: true
        }))
      }
    ]
  };
}

export function createActionMap(permissionTree, catalog = []) {
  const result = buildActionMapFromCatalog(catalog);

  return normalizePermissionTree(permissionTree).reduce((nextResult, group) => {
    group.children.forEach(child => {
      child.actions.forEach(action => {
        nextResult[action.uri] = Boolean(action.checked);
      });
    });

    return nextResult;
  }, result);
}

export function buildSidebarMenus(catalog, permissionTree) {
  const dashboardMenu = createDashboardMenu(catalog);
  const menus = normalizePermissionTree(permissionTree)
    .filter(group => group.checked !== false)
    .filter(group => group.uri !== DASHBOARD_GROUP_URI)
    .map(group => ({
      title: group.name,
      uri: group.uri,
      icon: group.icon,
      children: group.children
        .filter(child => child.checked !== false && child.path)
        .map(child => ({
          title: child.name,
          path: child.path,
          permissionUri: child.uri,
          actions: child.actions.map(action => ({
            code: action.code,
            uri: action.uri,
            name: action.name,
            checked: Boolean(action.checked)
          }))
        }))
    }))
    .filter(group => group.children.length > 0);

  return dashboardMenu ? [dashboardMenu].concat(menus) : menus;
}

export function buildPermissionOptions(catalog, permissionTree) {
  const childLookup = buildChildLookup(permissionTree);

  return catalog
    .filter(group => group.uri !== DASHBOARD_GROUP_URI)
    .map(group => ({
    code: group.code,
    label: group.title,
    children: group.children.map(child => {
      const mergedChild = mergeCatalogChild(child, childLookup[child.path] || childLookup[child.permissionUri]);

      return {
        code: mergedChild.code,
        label: mergedChild.title,
        path: mergedChild.path,
        children: mergedChild.actions.map(action => ({
          code: action.code,
          label: action.name,
          children: []
        }))
      };
    })
    }));
}
